import { Session } from '@supabase/supabase-js';
import { AppNotification, Category, FeatureRequest, Feedback, FeedbackComment, FollowPreset, FriendRequest, GoodShout, NotificationType, RecommendationStatus, RequestStatus, Shout, ShoutFollow, User, Venue } from '../types';
import { captureSilentError } from './posthog';
import { supabase } from './supabaseClient';

// -- HELPERS --

export const mapUser = (data: any): User => ({
  uid: data.uid,
  username: data.username,
  displayName: data.display_name,
  photoURL: data.photo_url,
  friends: [], // Populated separately
  emailConfirmedAt: data.email_confirmed_at, // This might come from auth.users, not public.users
  email: data.email, // Populated from auth user
  isAdmin: data.is_admin
});

export const mapVenue = (data: any): Venue => ({
  venueId: data.venue_id,
  name: data.name,
  lat: data.lat,
  lng: data.lng,
  category: data.category as Category,
  isCustom: data.is_custom,
  googlePlaceId: data.google_place_id
});

export const mapShout = (data: any): Shout => ({
  id: data.id,
  venueId: data.venue_id,
  userId: data.user_id,
  status: data.status as RecommendationStatus,
  description: data.description,
  createdAt: data.created_at
});

export const mapGoodShout = (record: any): GoodShout => ({
    id: record.id,
    shoutId: record.shout_id,
    userId: record.user_id,
    message: record.message,
    createdAt: record.created_at
});

export const mapRequest = (data: any): FriendRequest => ({
  id: data.id,
  senderId: data.sender_id,
  receiverId: data.receiver_id,
  status: data.status as RequestStatus,
  createdAt: data.created_at
});

export const mapFeedback = (data: any): Feedback => ({
  id: data.id,
  userId: data.user_id,
  type: data.type,
  description: data.description,
  status: data.status,
  createdAt: data.created_at
});

export const mapFeedbackComment = (data: any): FeedbackComment => ({
  id: data.id,
  feedbackId: data.feedback_id,
  userId: data.user_id,
  content: data.content,
  createdAt: data.created_at,
  user: data.user ? mapUser(data.user) : undefined
});

export const mapFeatureRequest = (data: any): FeatureRequest => ({
  id: data.id,
  userId: data.user_id,
  title: data.title,
  description: data.description,
  status: data.status,
  createdAt: data.created_at,
  voteCount: data.vote_count, // Requires join/count
  hasVoted: data.has_voted, // Requires check
  user: data.user_data ? mapUser(data.user_data) : undefined
});

export const mapFollow = (data: any): ShoutFollow => ({
  followerId: data.follower_id,
  followedId: data.followed_id,
  notifyPreset: data.notify_preset,
  createdAt: data.created_at
});

export const mapNotification = (data: any): AppNotification => ({
  id: data.id,
  userId: data.user_id,
  type: data.type as NotificationType,
  title: data.title,
  body: data.body,
  data: data.data,
  createdAt: data.created_at,
  readAt: data.read_at
});

// -- SERVICE --

export const db = {
  init: () => {
    // No-op for Supabase
  },

  // -- AUTH --
  
  login: async (email: string, password: string): Promise<User> => {
    // 1. Sign in with email
    
    const { data, error } = await supabase.auth.signInWithPassword({
      email: email,
      password: password,
    });

    if (error) throw error;
    if (!data.user) throw new Error("No user returned");

  // 2. Fetch Profile
  let { data: profile, error: profileError } = await supabase
    .from('users')
    .select('*')
    .eq('uid', data.user.id)
    .maybeSingle();

  if (profileError) throw profileError;

    // Self-healing: If profile missing (trigger failed?), create it now
    if (!profile) {
        console.warn("Profile missing for user, creating fallback...");
        const { data: newProfile, error: createError } = await supabase
            .from('users')
            .insert({
                uid: data.user.id,
                username: data.user.user_metadata.username || data.user.email?.split('@')[0] || 'user',
                display_name: data.user.user_metadata.display_name || 'User',
                photo_url: data.user.user_metadata.photo_url
            })
            .select()
            .single();
        
        if (createError) throw createError;
        profile = newProfile;
    }

    // 3. Fetch Friends (Mutual)
    const { data: friendships } = await supabase
      .from('friendships')
      .select('*')
      .or(`user_id_1.eq.${data.user.id},user_id_2.eq.${data.user.id}`);

    const user = mapUser(profile);
    user.friends = friendships?.map((f: any) => f.user_id_1 === data.user.id ? f.user_id_2 : f.user_id_1) || [];
    user.emailConfirmedAt = data.user.email_confirmed_at; // Get from Auth User object
    user.email = data.user.email;

    return user;
  },

  register: async (email: string, password: string, displayName: string, username: string): Promise<User> => {
    // 1. Sign up with Supabase Auth
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          username,
          display_name: displayName,
          photo_url: `https://api.dicebear.com/7.x/avataaars/svg?seed=${username}&backgroundColor=b6e3f4`
        }
      }
    });

    if (error) throw error;
    if (!data.user) throw new Error("Registration failed");

    // Wait for session to be established (sometimes RLS fails immediately after signUp)
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData.session) {
        await new Promise(resolve => setTimeout(resolve, 500));
    }

    // 2. Ensure Profile Exists (Manual Insert to be safe against trigger failures)
    const { error: profileError } = await supabase
        .from('users')
        .insert({
            uid: data.user.id,
            username,
            display_name: displayName,
            photo_url: `https://api.dicebear.com/7.x/avataaars/svg?seed=${username}&backgroundColor=b6e3f4`
        })
        .select()
        .single();

    // If insert fails (e.g. trigger already did it), we ignore error if it's a duplicate key
    if (profileError && profileError.code !== '23505') {
        console.warn("Manual profile creation failed:", profileError);
    }

    // 3. Return User
    return {
        uid: data.user.id,
        username,
        displayName,
        photoURL: `https://api.dicebear.com/7.x/avataaars/svg?seed=${username}&backgroundColor=b6e3f4`,
        friends: [],
        emailConfirmedAt: data.user.email_confirmed_at,
        email: data.user.email
    };
  },

  getSession: async (): Promise<User | null> => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return null;

    const { data: profile, error } = await supabase
      .from('users')
      .select('*')
      .eq('uid', session.user.id)
      .maybeSingle();

    if (error || !profile) return null;

    const { data: friendships } = await supabase
      .from('friendships')
      .select('*')
      .or(`user_id_1.eq.${session.user.id},user_id_2.eq.${session.user.id}`);

    const user = mapUser(profile);
    user.friends = friendships?.map((f: any) => f.user_id_1 === session.user.id ? f.user_id_2 : f.user_id_1) || [];
    user.emailConfirmedAt = session.user.email_confirmed_at; // Get from Auth User object
    user.email = session.user.email;

    return user;
  },

  getRawSession: async (): Promise<Session | null> => {
    const { data: { session } } = await supabase.auth.getSession();
    return session;
  },

  getUser: async (uid: string): Promise<User | undefined> => {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('uid', uid)
      .maybeSingle();
    
    if (error) return undefined;
    
    return data ? mapUser(data) : undefined;
  },

  logout: async () => {
    await supabase.auth.signOut();
  },

  updateProfile: async (updatedUser: User): Promise<User> => {
    const { data, error } = await supabase
      .from('users')
      .update({
        display_name: updatedUser.displayName,
        photo_url: updatedUser.photoURL,
        username: updatedUser.username
      })
      .eq('uid', updatedUser.uid)
      .select()
      .single();

    if (error) throw error;
    return { 
        ...mapUser(data), 
        friends: updatedUser.friends,
        email: updatedUser.email,
        emailConfirmedAt: updatedUser.emailConfirmedAt
    };
  },

  uploadProfileImage: async (userId: string, file: File): Promise<string> => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${userId}-${Date.now()}.${fileExt}`;
    const filePath = `${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('avatars')
      .upload(filePath, file);

    if (uploadError) {
      throw uploadError;
    }

    const { data } = supabase.storage
      .from('avatars')
      .getPublicUrl(filePath);

    return data.publicUrl;
  },

  deleteAccount: async (): Promise<void> => {
    const { error } = await supabase.rpc('delete_user');
    if (error) throw error;
  },

  resetPasswordForEmail: async (email: string): Promise<void> => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin, // Redirect back to the app
    });
    if (error) throw error;
  },

  updatePassword: async (newPassword: string): Promise<void> => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;
  },

  resendConfirmationEmail: async (email: string): Promise<void> => {
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: email,
    });
    if (error) throw error;
  },

  getUsersByIds: async (uids: string[]): Promise<User[]> => {
    if (!uids.length) return [];

    const { data, error } = await supabase
      .from('users')
      .select('*')
      .in('uid', uids);

    if (error) throw error;
    return data.map(mapUser);
  },

  // -- SOCIAL --

  searchUsers: async (query: string, currentUserId: string): Promise<User[]> => {
    const trimmed = query.trim();
    if (!trimmed) return [];

    const { data, error } = await supabase
      .from('users')
      .select('*')
      .neq('uid', currentUserId)
      .ilike('username', trimmed) // Require exact username (case-insensitive) - no autocomplete
      .limit(1);

    if (error) throw error;
    
    // Double check exact match in case ilike behaves loosely
    const exactMatches = data?.filter(u => u.username.toLowerCase() === trimmed.toLowerCase()) || [];
    
    return exactMatches.map(mapUser);
  },

  sendFriendRequest: async (senderId: string, receiverId: string): Promise<void> => {
    // Check if already friends
    const { data: existingFriendship } = await supabase
        .from('friendships')
        .select('*')
        .or(`and(user_id_1.eq.${senderId},user_id_2.eq.${receiverId}),and(user_id_1.eq.${receiverId},user_id_2.eq.${senderId})`)
        .maybeSingle();
    
    if (existingFriendship) throw new Error("Already friends");

    // Check pending
    const { data: pending } = await supabase
        .from('friend_requests')
        .select('*')
        .eq('sender_id', senderId)
        .eq('receiver_id', receiverId)
        .eq('status', RequestStatus.PENDING)
        .maybeSingle();

    if (pending) throw new Error("Request already pending");

    const { error } = await supabase
        .from('friend_requests')
        .insert({
            sender_id: senderId,
            receiver_id: receiverId,
            status: RequestStatus.PENDING
        });

    if (error) throw error;
  },

  getIncomingRequests: async (currentUserId: string): Promise<{ request: FriendRequest, fromUser: User }[]> => {
    const { data, error } = await supabase
        .from('friend_requests')
        .select(`
            *,
            from_user:users!sender_id(*)
        `)
        .eq('receiver_id', currentUserId)
        .eq('status', RequestStatus.PENDING);

    if (error) throw error;

    return data.map((r: any) => ({
        request: mapRequest(r),
        fromUser: mapUser(r.from_user)
    }));
  },

  getOutgoingRequests: async (currentUserId: string): Promise<{ request: FriendRequest, toUser: User }[]> => {
    const { data, error } = await supabase
        .from('friend_requests')
        .select(`
            *,
            to_user:users!receiver_id(*)
        `)
        .eq('sender_id', currentUserId)
        .eq('status', RequestStatus.PENDING);

    if (error) throw error;

    return data.map((r: any) => ({
        request: mapRequest(r),
        toUser: mapUser(r.to_user)
    }));
  },

  respondToRequest: async (requestId: string, accept: boolean): Promise<void> => {
    // 1. Update Request
    const status = accept ? RequestStatus.ACCEPTED : RequestStatus.REJECTED;
    const { data: request, error } = await supabase
        .from('friend_requests')
        .update({ status })
        .eq('id', requestId)
        .select()
        .single();

    if (error) throw error;

    if (accept && request) {
        // 2. Use RPC to create mutual friendship (bypassing RLS)
        const { error: rpcError } = await supabase.rpc('accept_friend_request', {
            request_id: requestId
        });
        
        if (rpcError) throw rpcError;
    }
  },

  // -- DATA --

  getFeed: async (currentUser: User): Promise<{ venues: Venue[], shouts: Shout[], users: User[], goodShouts: GoodShout[] }> => {
    // 1. Get list of relevant users (Me + Friends)
    const relevantUserIds = [currentUser.uid, ...currentUser.friends];

    // 2. Fetch Shouts from these users
    // Note: In a real large app, we'd paginate this.
    const { data: shoutsData, error: shoutsError } = await supabase
        .from('shouts')
        .select('*')
        .in('user_id', relevantUserIds);

    if (shoutsError) throw shoutsError;
    const shouts = shoutsData.map(mapShout);

    // 3. Prepare IDs for parallel fetches
    const venueIds = [...new Set(shouts.map(s => s.venueId))];
    const shoutIds = shouts.map(s => s.id);
    const allUserIds = new Set([
        ...shouts.map(s => s.userId),
        ...relevantUserIds // Ensure all relevant users are fetched
    ]);
    
    // 3. Fetch Venues, GoodShouts, and Users in parallel
    const [venuesResult, goodShoutsResult, usersResult] = await Promise.all([
        supabase.from('venues').select('*').in('venue_id', venueIds),
        supabase.from('good_shouts').select('*').in('shout_id', shoutIds).in('user_id', relevantUserIds),
        supabase.from('users').select('*').in('uid', Array.from(allUserIds))
    ]);

    if (venuesResult.error) throw venuesResult.error;
    if (goodShoutsResult.error) throw goodShoutsResult.error;
    if (usersResult.error) throw usersResult.error;

    const venues = venuesResult.data.map(mapVenue);
    const goodShouts = goodShoutsResult.data.map(mapGoodShout);
    const users = usersResult.data.map(mapUser);

    return {
        venues,
        shouts,
        users,
        goodShouts
    };
  },

  /**
   * Look up a venue by its Google place id, regardless of whose feed it is in.
   * Needed because `venues.google_place_id` is unique: a place first added by a
   * non-friend is invisible to this user's feed, so inserting it again would fail
   * with 23505. venues SELECT is public, so this works for any authenticated user.
   */
  findVenueByGooglePlaceId: async (googlePlaceId: string): Promise<Venue | null> => {
    const { data, error } = await supabase
        .from('venues')
        .select('*')
        .eq('google_place_id', googlePlaceId)
        .maybeSingle();

    if (error) throw error;
    return data ? mapVenue(data) : null;
  },

  addVenue: async (venue: Venue): Promise<void> => {
    const { error } = await supabase.from('venues').insert({
        venue_id: venue.venueId,
        name: venue.name,
        lat: venue.lat,
        lng: venue.lng,
        category: venue.category,
        is_custom: venue.isCustom,
        google_place_id: venue.googlePlaceId
    });
    if (error) throw error;
  },

  removeFriend: async (friendUid: string): Promise<void> => {
    const { error } = await supabase.rpc('remove_friend', { friend_uid: friendUid });
    if (error) throw error;
  },

  addShout: async (shout: Shout): Promise<void> => {
    const { error } = await supabase.from('shouts').insert({
        id: shout.id,
        venue_id: shout.venueId,
        user_id: shout.userId,
        status: shout.status,
        description: shout.description
    });
    if (error) throw error;
  },

  updateShout: async (updatedShout: Shout, category?: Category): Promise<void> => {
    const { error } = await supabase
        .from('shouts')
        .update({
            status: updatedShout.status,
            description: updatedShout.description
        })
        .eq('id', updatedShout.id);

    if (error) throw error;

    if (category) {
        const { error: venueError } = await supabase
            .from('venues')
            .update({ category })
            .eq('venue_id', updatedShout.venueId);
        
        if (venueError) throw venueError;
    }
  },

  deleteShout: async (shoutId: string): Promise<void> => {
    // First, get the venue ID before deleting
    const { data: shout } = await supabase
        .from('shouts')
        .select('venue_id')
        .eq('id', shoutId)
        .single();
    
    // Delete the shout
    const { error } = await supabase
        .from('shouts')
        .delete()
        .eq('id', shoutId);

    if (error) throw error;
    
    // Check if venue has any remaining shouts
    if (shout?.venue_id) {
        const { count } = await supabase
            .from('shouts')
            .select('*', { count: 'exact', head: true })
            .eq('venue_id', shout.venue_id);

        // If no shouts remain, delete the orphan venue
        if (count === 0) {
            await supabase
                .from('venues')
                .delete()
                .eq('venue_id', shout.venue_id);
        }
    }
  },

  addGoodShout: async (goodShout: GoodShout): Promise<void> => {
    const { error } = await supabase.from('good_shouts').insert({
        id: goodShout.id,
        shout_id: goodShout.shoutId,
        user_id: goodShout.userId,
        message: goodShout.message
    });
    if (error) throw error;
  },

  // -- NOTIFICATIONS --

  getShout: async (shoutId: string): Promise<Shout | null> => {
      const { data, error } = await supabase
          .from('shouts')
          .select('*')
          .eq('id', shoutId)
          .maybeSingle();
      
      if (error) return null;
      return data ? mapShout(data) : null;
  },

  subscribeToNotifications: (userId: string, onEvent: (type: 'REQUEST' | 'SHOUT', payload: any) => void) => {
      // 1. Subscribe to Friend Requests (INSERT where receiver_id = userId)
      const requestsSub = supabase
          .channel('friend-requests-channel')
          .on(
              'postgres_changes',
              {
                  event: 'INSERT',
                  schema: 'public',
                  table: 'friend_requests',
                  filter: `receiver_id=eq.${userId}`
              },
              async (payload) => {
                  // Fetch sender details for the notification
                  const senderId = payload.new.sender_id;
                  const sender = await db.getUser(senderId);
                  if (sender) {
                      onEvent('REQUEST', { ...payload.new, sender });
                  }
              }
          )
          .subscribe();

      // 2. Subscribe to ShoutOuts (INSERT)
      const shoutoutsSub = supabase
          .channel('good-shouts-channel')
          .on(
              'postgres_changes',
              {
                  event: 'INSERT',
                  schema: 'public',
                  table: 'good_shouts'
              },
              async (payload) => {
                  // Check if the shout belongs to us
                  const shoutId = payload.new.shout_id;
                  const shout = await db.getShout(shoutId);
                  
                  if (shout && shout.userId === userId && payload.new.user_id !== userId) {
                      // It's for us!
                      const sender = await db.getUser(payload.new.user_id);
                      if (sender) {
                          onEvent('SHOUT', { ...payload.new, sender, shout });
                      }
                  }
              }
          )
          .subscribe();

      return () => {
          supabase.removeChannel(requestsSub);
          supabase.removeChannel(shoutoutsSub);
      };
  },

  // -- FOLLOWS --
  getFollows: async (userId: string): Promise<ShoutFollow[]> => {
    const { data, error } = await supabase
      .from('shout_follows')
      .select('*')
      .eq('follower_id', userId);
    
    if (error) throw error;
    return data.map(mapFollow);
  },

  followUser: async (followerId: string, followedId: string, preset: FollowPreset): Promise<void> => {
    const { error } = await supabase
      .from('shout_follows')
      .insert({
        follower_id: followerId,
        followed_id: followedId,
        notify_preset: preset
      });
    
    if (error) throw error;
  },

  unfollowUser: async (followerId: string, followedId: string): Promise<void> => {
    const { error } = await supabase
      .from('shout_follows')
      .delete()
      .eq('follower_id', followerId)
      .eq('followed_id', followedId);
      
    if (error) throw error;
  },

  updateFollowPreset: async (followerId: string, followedId: string, preset: FollowPreset): Promise<void> => {
    const { error } = await supabase
      .from('shout_follows')
      .update({ notify_preset: preset })
      .eq('follower_id', followerId)
      .eq('followed_id', followedId);
      
    if (error) throw error;
  },

  savePushSubscription: async (userId: string, subscription: PushSubscription): Promise<void> => {
      const { endpoint, keys } = subscription.toJSON();
      if (!endpoint || !keys || !keys.p256dh || !keys.auth) return;

      const { error } = await supabase
          .from('push_subscriptions')
          .upsert({
              user_id: userId,
              endpoint,
              p256dh: keys.p256dh,
              auth: keys.auth
          }, { onConflict: 'endpoint' });

      if (error) captureSilentError('db.savePushSubscription', error);
  },

  // -- FEEDBACK & SUPPORT --

  submitFeedback: async (userId: string, type: 'good' | 'bad', description: string): Promise<void> => {
    const { error } = await supabase.from('feedback').insert({
      user_id: userId,
      type,
      description
    });
    if (error) throw error;
  },

  getFeedback: async (isAdmin: boolean, userId: string, limit = 50, offset = 0): Promise<Feedback[]> => {
    let query = supabase.from('feedback').select('*').order('created_at', { ascending: false });
    
    // RLS handles the filtering, but we can be explicit
    if (!isAdmin) {
      query = query.eq('user_id', userId);
    }

    const { data, error } = await query.range(offset, offset + limit - 1);
    if (error) throw error;
    return data.map(mapFeedback);
  },

  addFeedbackComment: async (feedbackId: string, userId: string, content: string): Promise<void> => {
    const { error } = await supabase.from('feedback_comments').insert({
      feedback_id: feedbackId,
      user_id: userId,
      content
    });
    if (error) throw error;
  },

  getFeedbackComments: async (feedbackId: string): Promise<FeedbackComment[]> => {
    const { data, error } = await supabase
      .from('feedback_comments')
      .select(`
        *,
        user:users(*)
      `)
      .eq('feedback_id', feedbackId)
      .order('created_at', { ascending: true });

    if (error) throw error;
    return data.map(mapFeedbackComment);
  },

  // -- FEATURE REQUESTS --

  submitFeatureRequest: async (userId: string, title: string, description: string): Promise<void> => {
    // Check limit: Max 3 active requests per month (simplification: just max 3 active requests total for now, or check last month)
    // The prompt says: "at most 3 features in one month before they get an error... _or_ for one of their suggestions to be moved to 'in progress'"
    // Let's check active requests (suggested) count first.
    
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);

    const { count, error: countError } = await supabase
      .from('feature_requests')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId)
      .eq('status', 'suggested')
      .gte('created_at', oneMonthAgo.toISOString());

    if (countError) throw countError;

    if (count !== null && count >= 3) {
      throw new Error("You have reached the limit of 3 feature suggestions per month. Please wait or wait for a suggestion to be picked up.");
    }

    const { error } = await supabase.from('feature_requests').insert({
      user_id: userId,
      title,
      description
    });
    if (error) throw error;
  },

  getFeatureRequests: async (currentUserId?: string, limit = 50, offset = 0): Promise<FeatureRequest[]> => {
    if (!currentUserId) return [];

    const { data, error } = await supabase.rpc('get_feature_requests', {
        current_user_id: currentUserId,
        limit_count: limit,
        offset_count: offset
    });

    if (error) throw error;

    return data.map((row: any) => ({
        id: row.id,
        userId: row.user_id,
        title: row.title,
        description: row.description,
        status: row.status,
        createdAt: row.created_at,
        voteCount: Number(row.vote_count),
        hasVoted: row.has_voted,
        user: row.user_data ? mapUser(row.user_data) : undefined
    }));
  },

  voteFeature: async (featureId: string, userId: string): Promise<void> => {
    // Check if already voted
    const { error } = await supabase.from('feature_votes').insert({
      feature_id: featureId,
      user_id: userId
    });
    
    // Ignore duplicate key error (already voted)
    if (error && error.code !== '23505') throw error;
  },

  removeVote: async (featureId: string, userId: string): Promise<void> => {
    const { error } = await supabase
      .from('feature_votes')
      .delete()
      .eq('feature_id', featureId)
      .eq('user_id', userId);
    
    if (error) throw error;
  },

  updateFeatureStatus: async (featureId: string, status: 'suggested' | 'in_progress' | 'complete'): Promise<void> => {
    const { error } = await supabase
      .from('feature_requests')
      .update({ status })
      .eq('id', featureId);
    
    if (error) throw error;
  },

  deleteFeatureRequest: async (featureId: string): Promise<void> => {
    const { error } = await supabase
        .from('feature_requests')
        .delete()
        .eq('id', featureId);

    if (error) throw error;
  },

  // -- IN-APP NOTIFICATIONS --

  getNotifications: async (userId: string): Promise<AppNotification[]> => {
    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(100);

    if (error) throw error;
    return data.map(mapNotification);
  },

  getUnreadNotificationCount: async (userId: string): Promise<number> => {
    const { count, error } = await supabase
      .from('notifications')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId)
      .is('read_at', null);

    if (error) throw error;
    return count || 0;
  },

  markNotificationRead: async (notificationId: string): Promise<void> => {
    const { error } = await supabase
      .from('notifications')
      .update({ read_at: new Date().toISOString() })
      .eq('id', notificationId);

    if (error) throw error;
  },

  markAllNotificationsRead: async (userId: string): Promise<void> => {
    const { error } = await supabase
      .from('notifications')
      .update({ read_at: new Date().toISOString() })
      .eq('user_id', userId)
      .is('read_at', null);

    if (error) throw error;
  }
};
